import Typography from '@mui/material/Typography'
import { Editor } from '@tinymce/tinymce-react'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import ConfirmDialog from './ConfirmDialog'
import SpinnerDialog from './SpinnerDialog'
import QRCodeDialog from './QRCodeDialog'
import AttachmentDialog from './AttachmentDialog'

const TextEditor = forwardRef(
  (
    {
      error,
      isSms = false,
      isMobile = false,
      templateOptions,
      placeholderOptions,
      restoreContent,
      isQRcode = false,
      imageUploadCallback,
      onContentChange,
      onInit,
      dispatch,
      maxLength,
    },
    ref,
  ) => {
    const [showRestore, setShowRestore] = useState(false)
    const editorRef = useRef()
    const restoreButtonRef = useRef()
    const restoreContentRef = useRef()
    const [urlList, setUrlList] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isQRDialogOpen, setIsQRDialogOpen] = useState(false)
    const [isAttachmentDialogOpen, setIsAttachmentDialogOpen] = useState(false)
    const [content, setContent] = useState()

    const { t } = useTranslation()

    useImperativeHandle(ref, () => ({ editor: () => editorRef.current, urlList: () => urlList }))

    const handleGenerateQRCode = imageUrl => {
      const editor = editorRef.current
      const imgTag = `<img ${imageUrl} />`
      editor.insertContent(imgTag)
    }

    const handleGenerateAttachment = imageUrl => {
      const editor = editorRef.current
      const imgTag = `${imageUrl}`
      editor.insertContent(imgTag)
    }

    const handleOpenQRDialog = () => {
      setIsQRDialogOpen(true)
    }

    const handleCloseQRDialog = () => {
      setIsQRDialogOpen(false)
    }

    const handleOpenAttachmentDialog = () => {
      setIsAttachmentDialogOpen(true)
    }

    const handleCloseAttachmentDialog = () => {
      setIsAttachmentDialogOpen(false)
    }

    const resizeContentArea = () => {
      if (!isMobile) return

      const editor = editorRef.current

      if (editor) {
        const parentStyle = editor.contentAreaContainer.parentElement.style
        parentStyle.backgroundColor = 'hsl(0,0%,96.5%)'
        parentStyle.justifyContent = 'center'

        const contentAreaStyle = editor.contentAreaContainer.style
        contentAreaStyle.aspectRatio = '9/16'
        // contentAreaStyle.width = '100px'
        contentAreaStyle.flex = 'unset'
      }
    }

    const insertLinkDialog = {
      title: t('tinymce.toolbar.insert_link'),
      body: {
        type: 'panel',
        items: isSms
          ? []
          : [
              {
                type: 'input',
                name: 'displayText',
                label: t('tinymce.dialogs.insert_link.display_text'),
              },
            ],
      },
      buttons: [
        { type: 'cancel', name: 'closeBtn', text: t('general.cancel') },
        {
          type: 'submit',
          name: 'submitBtn',
          text: t(isSms ? 'general.confirm' : 'general.save'),
          buttonType: 'primary',
        },
      ],
      onSubmit: api => {
        const data = api.getData()
        const displayText = data.displayText || ''
        const html = `<a data-mce-href="{{landing_page_url}}" href="{{landing_page_url}}">${displayText}</a>`
        editorRef.current?.execCommand(
          'mceInsertContent',
          false,
          isSms ? '{{landing_page_url}}' : html,
        )
        api.close()
      },
    }

    useEffect(() => {
      if (typeof restoreContent === 'string') {
        if (isSms) {
          const formattedContent = restoreContent.replaceAll(' ', '&nbsp;')
          const temp = formattedContent.replaceAll('\n', '<br />')
          setContent(temp)
          editorRef.current?.setContent(temp)
        } else {
          restoreContentRef.current = restoreContent
          editorRef.current?.setContent(restoreContent)
        }
      }
      restoreButtonRef.current?.setEnabled?.(!!restoreContent)
    }, [restoreContent, isSms])

    return (
      <>
        <Editor
          onInit={(_, editor) => {
            editorRef.current = editor
            resizeContentArea()
            onInit?.(editor)
          }}
          value={content}
          onEditorChange={(val, editor) => {
            if (maxLength) {
              const charCount = editor.plugins.wordcount.body.getCharacterCount()

              if (charCount > maxLength) {
                dispatch({
                  type: 'app/notify',
                  payload: {
                    message: t('messages.char_limit', { limit: maxLength }),
                    severity: 'error',
                  },
                })

                return
              }
            }
            setContent(val)
          }}
          tinymceScriptSrc={'/tinymce/tinymce.min.js'}
          initialValue={`
<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
${isMobile ? '<meta name="viewport" content="width=device-width, initial-scale=1.0">' : ''}
</head>
<body>

</body>
</html>
          `}
          init={{
            relative_urls: false,
            convert_urls: false,
            height: 500,
            menubar: false,
            skin: 'oxide',
            language: 'zh_TW',
            language_url: '/langs/zh-Hant.js',
            image_title: true,
            typeahead_urls: false,
            forced_root_block: isSms ? '' : 'p',
            invalid_elements: isSms ? 'p, wbr, span' : '',
            resize_img_proportional: false,
            file_picker_types: 'image',
            file_picker_callback: cb => {
              const input = document.createElement('input')
              input.setAttribute('type', 'file')
              input.setAttribute('accept', 'image/*')

              /*
              Note: In modern browsers input[type="file"] is functional without
              even adding it to the DOM, but that might not be the case in some older
              or quirky browsers like IE, so you might want to add it to the DOM
              just in case, and visually hide it. And do not forget do remove it
              once you do not need it anymore.
            */
              input.onchange = function inputOnchange() {
                const file = this.files[0]

                if (file.size > 31457280) {
                  dispatch({
                    type: 'app/notify',
                    payload: {
                      message: t('messages.image_size_limit'),
                      severity: 'error',
                    },
                  })

                  return
                }

                if (imageUploadCallback) {
                  setIsLoading(true)
                }
                imageUploadCallback?.(
                  file,
                  url => {
                    if (url) {
                      setUrlList([...urlList, url])
                      cb(url, {
                        title: file.name,
                      })
                    }

                    setIsLoading(false)
                  },
                  err => {
                    dispatch({
                      type: 'app/notify',
                      payload: {
                        message: err,
                        severity: 'error',
                      },
                    })

                    setIsLoading(false)
                  },
                )
              }

              input.click()
            },
            init_instance_callback: editor => {
              const wordCountBtn = editor
                .getContainer()
                .getElementsByClassName('tox-statusbar__wordcount')?.[0]

              wordCountBtn?.click()
            },
            setup: editor => {
              editor.on('ResizeEditor', () => {
                resizeContentArea()
              })

              editor.on('OpenWindow', e => {
                const dialogData = e.dialog.getData()

                const { alt, dimensions, src, title } = dialogData

                if (
                  alt !== undefined &&
                  dimensions !== undefined &&
                  src !== undefined &&
                  title !== undefined
                ) {
                  const dialogElement = document.getElementsByClassName('tox-dialog')?.[0]
                  const inputElement = dialogElement?.getElementsByTagName('input')?.[0]

                  inputElement.disabled = true
                }
              })

              editor.on('Change', e => {
                const changedContent = e.level.content
                onContentChange?.(changedContent)
              })

              editor.ui.registry.addIcon(
                'link',
                `<svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" focusable="false">
                <g id="Icon/Add-landing" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                <polygon id="Path" points="0 0 24 0 24 24 0 24"></polygon>
                <path d="M21,3 L3,3 C1.9,3 1,3.9 1,5 L1,8 L3,8 L3,5 L21,5 L21,19 L20,19 L20,21 L21,21 C22.1,21 23,20.1 23,19 L23,5 C23,3.9 22.1,3 21,3 Z" id="Shape" fill="#15191D" fill-rule="nonzero" transform="translate(12.000000, 12.000000) scale(-1, 1) translate(-12.000000, -12.000000) "></path>
                <rect id="Rectangle" fill="#15191D" fill-rule="nonzero" x="21" y="7" width="2" height="4"></rect>
                <path d="M7.91764706,17 C7.91764706,15.5917647 9.06235294,14.4470588 10.4705882,14.4470588 L13.7647059,14.4470588 L13.7647059,12.8823529 L10.4705882,12.8823529 C8.19764706,12.8823529 6.35294118,14.7270588 6.35294118,17 C6.35294118,19.2729412 8.19764706,21.1176471 10.4705882,21.1176471 L13.7647059,21.1176471 L13.7647059,19.5529412 L10.4705882,19.5529412 C9.06235294,19.5529412 7.91764706,18.4082353 7.91764706,17 Z M11.2941176,17.8235294 L17.8823529,17.8235294 L17.8823529,16.1764706 L11.2941176,16.1764706 L11.2941176,17.8235294 Z M18.7058824,12.8823529 L15.4117647,12.8823529 L15.4117647,14.4470588 L18.7058824,14.4470588 C20.1141176,14.4470588 21.2588235,15.5917647 21.2588235,17 C21.2588235,18.4082353 20.1141176,19.5529412 18.7058824,19.5529412 L15.4117647,19.5529412 L15.4117647,21.1176471 L18.7058824,21.1176471 C20.9788235,21.1176471 22.8235294,19.2729412 22.8235294,17 C22.8235294,14.7270588 20.9788235,12.8823529 18.7058824,12.8823529 Z" id="Shape" fill="#15191D" fill-rule="nonzero"></path>
                <rect id="Rectangle" fill="#15191D" x="2" y="7" width="20" height="2"></rect>
                </g>
                </svg>`,
              )

              editor.ui.registry.addIcon(
                `QRcodeI`,
                `<svg width="24px" height="24px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M3 9h6V3H3zm1-5h4v4H4zm1 1h2v2H5zm10 4h6V3h-6zm1-5h4v4h-4zm1 1h2v2h-2zM3 21h6v-6H3zm1-5h4v4H4zm1 1h2v2H5zm15 2h1v2h-2v-3h1zm0-3h1v1h-1zm0-1v1h-1v-1zm-10 2h1v4h-1v-4zm-4-7v2H4v-1H3v-1h3zm4-3h1v1h-1zm3-3v2h-1V3h2v1zm-3 0h1v1h-1zm10 8h1v2h-2v-1h1zm-1-2v1h-2v2h-2v-1h1v-2h3zm-7 4h-1v-1h-1v-1h2v2zm6 2h1v1h-1zm2-5v1h-1v-1zm-9 3v1h-1v-1zm6 5h1v2h-2v-2zm-3 0h1v1h-1v1h-2v-1h1v-1zm0-1v-1h2v1zm0-5h1v3h-1v1h-1v1h-1v-2h-1v-1h3v-1h-1v-1zm-9 0v1H4v-1zm12 4h-1v-1h1zm1-2h-2v-1h2zM8 10h1v1H8v1h1v2H8v-1H7v1H6v-2h1v-2zm3 0V8h3v3h-2v-1h1V9h-1v1zm0-4h1v1h-1zm-1 4h1v1h-1zm3-3V6h1v1z"></path><path fill="none" d="M0 0h24v24H0z"></path></g></svg>`,
              )

              editor.ui.registry.addButton('QRCodeButton', {
                icon: `QRcodeI`,
                tooltip: t('tinymce.toolbar.qrcode'),
                onAction: handleOpenQRDialog,
              })

              editor.ui.registry.addButton('attachmentButton', {
                icon: 'media',
                tooltip: t('tinymce.toolbar.attachment'),
                onAction: handleOpenAttachmentDialog,
              })

              editor.ui.registry.addIcon(
                'unlink',
                `<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor" focusable="false"><path d="M0 0h24v24H0V0z" fill="none"></path><path d="M17 7h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1 0 1.43-.98 2.63-2.31 2.98l1.46 1.46C20.88 15.61 22 13.95 22 12c0-2.76-2.24-5-5-5zm-1 4h-2.19l2 2H16zM2 4.27l3.11 3.11C3.29 8.12 2 9.91 2 12c0 2.76 2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1 0-1.59 1.21-2.9 2.76-3.07L8.73 11H8v2h2.73L13 15.27V17h1.73l4.01 4L20 19.74 3.27 3 2 4.27z"></path><path d="M0 24V0" fill="none"></path></svg>`,
              )

              editor.ui.registry.addButton('restore', {
                icon: 'rotate-left',
                tooltip: t('tinymce.toolbar.restore'),
                enabled: typeof restoreContentRef.current === 'string',
                onAction: () => {
                  setShowRestore(true)
                },
                onSetup: buttonApi => {
                  restoreButtonRef.current = buttonApi
                },
              })

              editor.ui.registry.addButton('insert-link', {
                icon: 'link',
                tooltip: t('tinymce.toolbar.insert_link'),
                onAction: () => {
                  editor.windowManager.open(insertLinkDialog)
                },
              })

              editor.ui.registry.addMenuButton('placeholder', {
                text: t('tinymce.toolbar.placeholder'),
                tooltip: t('tinymce.toolbar.placeholder_tooltip'),
                fetch: callback => {
                  const items = placeholderOptions.map(option => ({
                    type: 'menuitem',
                    text: option.text,
                    onAction: () => {
                      editor.insertContent(`{{${option.value}}}`)
                    },
                  }))

                  callback(items)
                },
              })
            },
            plugins: [
              'advlist',
              'autolink',
              'lists',
              'link',
              'image',
              'charmap',
              'preview',
              'anchor',
              'searchreplace',
              'visualblocks',
              'code',
              'fullscreen',
              'insertdatetime',
              'media',
              'table',
              'wordcount',
              'directionality',
              'template',
              ...(isSms ? [] : ['fullpage']),
              'hr',
              'paste',
            ],
            toolbar: isSms
              ? ['fullscreen | placeholder | copy paste | insert-link |', 'undo redo restore | ']
              : [
                  `fullscreen code |${
                    isQRcode ? 'QRCodeButton attachmentButton customImageInsertButton' : ''
                  } image ${placeholderOptions ? 'placeholder ' : ''}| ` +
                    'copy paste ltr rtl | ' +
                    'insert-link unlink hr removeformat |' +
                    `${templateOptions ? 'template | ' : ''}formatselect | ` +
                    'fontselect fontsizeselect',
                  'undo redo restore | ' +
                    'forecolor backcolor bold italic | ' +
                    'alignleft aligncenter alignright alignjustify | ' +
                    'bullist numlist outdent indent | ' +
                    'table',
                ],
            templates: templateOptions,
            content_style: `body {font - family:Helvetica,Arial,sans-serif; font-size:14px } ${
              isMobile ? 'img { max-width: 100%; object-fit: contain;' : ''
            }}`,
            advlist_bullet_styles: 'default',
            advlist_number_styles: 'default',
            paste_data_images: false,
            paste_preprocess: (_, args) => {
              if (
                args.content.includes('<img src="blob:') ||
                args.content.includes('<img src="data:')
              ) {
                args.content = ''
              }
            },
          }}
        />

        {error && (
          <Typography fontSize="small" color="error">
            {error}
          </Typography>
        )}

        <ConfirmDialog
          open={!!showRestore}
          onClose={() => {
            setShowRestore(false)
          }}
          onSubmit={() => {
            editorRef.current.setContent(restoreContentRef.current)
            setShowRestore(false)
          }}
        >
          <Typography>{t('messages.restore_email_template')}</Typography>
        </ConfirmDialog>
        <SpinnerDialog open={isLoading} />
        <QRCodeDialog
          isOpen={isQRDialogOpen}
          onClose={handleCloseQRDialog}
          onGenerateQRCode={handleGenerateQRCode}
        />
        <AttachmentDialog
          isOpen={isAttachmentDialogOpen}
          onClose={handleCloseAttachmentDialog}
          onGenerateFakeAttachment={handleGenerateAttachment}
        />
      </>
    )
  },
)

export default connect(() => ({}), null, null, { forwardRef: true })(TextEditor)
